import { useEffect, useState } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertSuccessIcon,
  Circle,
  Grid,
  GridItem,
  HStack,
  MenuSearchBoxOption,
  Text,
} from '@cdl/heartbeat-react';
import { NumberForm } from './inputs/NumberForm';
import { DropdownSingleData } from './dropdown/DropdownSingleData';
import { FormValues } from '../../types/annotation-task-detail.type';
import { getAllDataset } from '../../clients/annotation-task.client';
import { ClearButton } from './ClearButton';

type Props = {
  register: UseFormRegister<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  control: Control<FormValues>;
  resetField: UseFormResetField<FormValues>;
};

export const HoneypotForm: React.FC<Props> = ({
  register,
  setValue,
  control,
  resetField,
}) => {
  const [honeyPotDatasetList, setHoneyPotDatasetList] = useState<
    MenuSearchBoxOption[]
  >([]);

  const handleHoneyPotDatasetChange = (value: string) => {
    setValue('honeypot_dataset_id', value, { shouldValidate: true });
  };

  useEffect(() => {
    const fetchDataset = async () => {
      setHoneyPotDatasetList(await getAllDataset());
    };
    fetchDataset();
  }, []);

  const clearAllData = () => {
    resetField('honeypot_dataset_id');
    resetField('honeypot_probability');
  };

  const [honeypot_probability, honeypot_dataset_id] = useWatch({
    control,
    name: ['honeypot_probability', 'honeypot_dataset_id'],
  });

  const filledFields = [honeypot_probability, honeypot_dataset_id].filter(
    (value) => value !== '' && value !== undefined && value !== 0,
  );

  const displayFields = [
    { name: 'Honeypot Probability', value: honeypot_probability },
    { name: 'Honeypot Dataset ID', value: honeypot_dataset_id },
  ]
    .filter((field) => filledFields.includes(field.value))
    .map((field) => `${field.name}: ${field.value}`)
    .join(' - ');

  let statusIcon;
  let statusText;
  switch (filledFields.length) {
    case 2:
      statusIcon = <AlertSuccessIcon size='s' color='green.400' m='s' />;
      statusText = <Text color='neutral.300'>{displayFields}</Text>;
      break;
    case 1:
      statusIcon = <AlertSuccessIcon size='s' color='orange.400' m='s' />;
      statusText = <Text color='neutral.300'>{displayFields}</Text>;
      break;
    default:
      statusIcon = <Circle size='16px' m='s' border='1px' />;
      statusText = (
        <Text pl='xl' color='neutral.300'>
          No honeypot set
        </Text>
      );
      break;
  }

  return (
    <Accordion defaultIndex={[0]} allowMultiple>
      <AccordionItem bgColor='white'>
        <AccordionButton
          justifyContent='space-between'
          p='l'
          borderBottom='1px'
          borderColor='neutral.100'
        >
          <HStack gap={2}>
            {statusIcon}
            <Text textStyle='page-subtitle' as='span'>
              Honeypot
            </Text>
            <HStack pl='xl'>{statusText}</HStack>
          </HStack>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel p='xl' h='auto'>
          <Grid templateColumns='repeat(3, 1fr)' gap={2}>
            <GridItem colSpan={2}>
              <DropdownSingleData
                options={honeyPotDatasetList}
                handleDataChange={handleHoneyPotDatasetChange}
                register={register}
                type='honeypot_dataset_id'
                control={control}
              />
            </GridItem>
            <GridItem colSpan={1}>
              <NumberForm
                type='honeypot_probability'
                control={control}
                step={0.1}
                max={1}
              />
            </GridItem>
          </Grid>
          <ClearButton clearAllData={clearAllData} />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
